import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Loader2, CheckCircle, XCircle, ShoppingBag, CreditCard, Shield, Crown, Wallet, Copy, Upload, Clock, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEMBERSHIP_PRICES = {
  'og-access': { name: 'OG Access Membership', price: 14.99 },
  'og-pro': { name: 'OG Pro Membership', price: 35.99 },
  'og-unlimited': { name: 'OG Unlimited Membership', price: 99 }
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemDetails, setItemDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Crypto state
  const [cryptoMethod, setCryptoMethod] = useState('usdt');
  const [cryptoOrder, setCryptoOrder] = useState(null);
  const [wallets, setWallets] = useState({});
  const [proofValue, setProofValue] = useState('');
  const [proofSubmitted, setProofSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const itemType = searchParams.get('type');
  const itemId = searchParams.get('id');
  const cartIds = searchParams.get('ids'); // For multi-item cart
  const licenseType = searchParams.get('license') || 'basic';
  const sessionId = searchParams.get('session_id');
  const [cartItems, setCartItems] = useState([]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user && sessionId !== 'PAYPAL') {
      const redirectUrl = `/checkout?type=${itemType}&id=${itemId}&license=${licenseType}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
    }
  }, [authLoading, user, sessionId, navigate, itemType, itemId, licenseType]);

  useEffect(() => {
    if (sessionId === 'PAYPAL') {
      setPaymentSuccess(true);
      setItemDetails({ name: 'Payment Successful', status: 'success' });
    } else {
      fetchItemDetails();
      fetchWallets();
      fetchPaypalClientId();
    }
  }, [sessionId]);

  const fetchPaypalClientId = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/checkout/paypal-client-id`);
      if (res.data.client_id) setPaypalClientId(res.data.client_id);
    } catch {}
  };

  const fetchWallets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crypto/wallets`);
      setWallets(res.data);
    } catch {}
  };

  const fetchItemDetails = async () => {
    if (itemType === 'cart' && cartIds) {
      // Multi-item cart
      try {
        const ids = cartIds.split(',');
        const items = [];
        let total = 0;
        for (const id of ids) {
          const r = await axios.get(`${API_URL}/api/tracks/${id}`);
          const track = r.data;
          const price = track.price || 0;
          items.push({ id, name: track.title, price, genre: track.genre, original_price: track.original_price, discount_pct: track.discount_pct });
          total += price;
        }
        setCartItems(items);
        setItemDetails({
          name: `Carrito (${items.length} tracks)`,
          price: Math.round(total * 100) / 100,
          description: items.map(i => i.name).join(', '),
          licenseLabel: 'Basic License',
          isCart: true,
        });
      } catch { setError('Error cargando items del carrito'); }
      return;
    }
    if (!itemType || !itemId) { setError('Invalid checkout parameters'); return; }
    try {
      let details = null;
      if (itemType === 'track') {
        const response = await axios.get(`${API_URL}/api/tracks/${itemId}`);
        const track = response.data;
        if (licenseType === 'exclusive') {
          details = { name: `${track.title} - Exclusive License`, price: 99.99, image: track.cover_image, description: 'Unlimited downloads, full usage control, track removed from catalog', licenseLabel: 'Exclusive License' };
        } else {
          details = { name: `${track.title} - Basic License`, price: track.price, image: track.cover_image, description: 'Commercial use allowed, worldwide rights, full freedom of use', licenseLabel: 'Basic License (Included)' };
        }
      } else if (itemType === 'pack') {
        if (itemId === 'ultimate') {
          const response = await axios.get(`${API_URL}/api/packs/ultimate`);
          details = { name: response.data.name, price: response.data.price, image: response.data.cover_image, description: 'Multiple ready-to-release songs with full commercial rights and playlist ecosystem access' };
        } else if (itemId === 'artist-launch') {
          const response = await axios.get(`${API_URL}/api/packs/artist-launch`);
          details = { name: response.data.name, price: response.data.price, image: response.data.cover_image, description: '15 songs, album artwork, artist branding, distribution guidance, and launch strategy' };
        } else {
          const response = await axios.get(`${API_URL}/api/packs/${itemId}`);
          details = { name: response.data.name, price: response.data.price, image: response.data.cover_image, description: response.data.description };
        }
      } else if (itemType === 'membership') {
        // Try local hardcoded map first; otherwise fetch dynamic list (handles og-ultimate, future tiers)
        let tier = MEMBERSHIP_PRICES[itemId];
        if (!tier) {
          try {
            const r = await axios.get(`${API_URL}/api/memberships/`);
            const found = (r.data || []).find(t => t.id === itemId);
            if (found) tier = { name: `${found.name} Membership`, price: found.price };
          } catch { /* ignore — caught below */ }
        }
        if (tier) {
          details = {
            name: tier.name,
            price: tier.price,
            description: 'Suscripción mensual con licencia comercial, renovación automática y cancelación cuando quieras',
          };
        }
      } else if (itemType === 'mastering_og') {
        // OG Mastering automated job
        const r = await axios.get(`${API_URL}/api/mastering-og/jobs/${itemId}/status`, { withCredentials: true });
        const og = r.data;
        details = {
          name: `OG Mastering ${og.plan_name}`,
          price: og.price,
          description: `${og.total_tracks} tracks · 8 presets desbloqueados · +5 tracks bonus de regalo en tu primera compra`,
          licenseLabel: og.plan_name,
        };
      } else if (itemType === 'playlist_promo') {
        const r = await axios.get(`${API_URL}/api/playlist-promo/${itemId}/status`, { withCredentials: true });
        const promo = r.data;
        details = {
          name: `OG Playlist Promotion · ${promo.plan_name}`,
          price: promo.price,
          description: `${promo.playlists_count} playlists OG · ${promo.duration_days} días garantía · Revisión en ${promo.review_hours}h${promo.track_title ? ` · ${promo.track_title}` : ''}`,
          licenseLabel: `Plan ${promo.plan_name}`,
        };
      } else if (itemType === 'mastering') {
        // Fetch the mastering order
        const r = await axios.get(`${API_URL}/api/mastering/order/${itemId}`, { withCredentials: true });
        const mo = r.data;
        details = {
          name: `Mastering ${mo.tier_name} - ${mo.song_name}`,
          price: mo.price,
          description: `Servicio de masterizacion profesional. Tras el pago podras subir tu archivo MP3.`,
          licenseLabel: `Plan ${mo.tier_name}`,
        };
      } else if (itemType === 'mastering_playlist') {
        const r = await axios.get(`${API_URL}/api/mastering/order/${itemId}`, { withCredentials: true });
        const mo = r.data;
        details = {
          name: `Playlist Access - ${mo.song_name}`,
          price: 29.99,
          description: 'Acceso al Ecosistema de Playlists OG MUSIC por 30 dias. Renovable.',
          licenseLabel: 'Add-on 30 dias',
        };
      }
      if (!details) { setError('Item not found'); return; }
      setItemDetails(details);
    } catch { setError('Failed to load item details'); }
  };

  const handleCryptoOrder = async () => {
    setLoading(true); setError('');
    try {
      const payload = itemType === 'cart'
        ? { item_type: 'cart', cart_ids: cartIds?.split(',') || [], license_type: licenseType, crypto_method: cryptoMethod }
        : { item_type: itemType, item_id: itemId, license_type: licenseType, crypto_method: cryptoMethod };
      const response = await axios.post(`${API_URL}/api/crypto/order`, payload, { withCredentials: true });
      setCryptoOrder(response.data);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create crypto order'); }
    setLoading(false);
  };

  const handleSubmitProof = async () => {
    if (!proofValue.trim()) { setError('Please enter a Transaction ID or proof'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/crypto/proof`, { order_id: cryptoOrder.order_id, proof_type: 'txid', proof_value: proofValue.trim() });
      setProofSubmitted(true);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to submit proof'); }
    setLoading(false);
  };

  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Treat memberships and Ultimate Pack as PayPal recurring subscriptions
  const isSubscriptionFlow = (
    itemType === 'membership' ||
    (itemType === 'pack' && itemId === 'ultimate')
  );
  const subscriptionTierId = itemType === 'membership' ? itemId : (itemType === 'pack' && itemId === 'ultimate' ? 'ultimate' : '');

  // === Success State ===
  if (paymentSuccess || (sessionId === 'PAYPAL')) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="glass-card p-12 text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-4" data-testid="checkout-success-title">{t('checkout.success')}</h1>
          <p className="text-white/60 mb-8">{t('checkout.successMessage')}</p>
          <div className="flex flex-col gap-4">
            <Link to="/catalog" className="btn-primary py-3" data-testid="checkout-continue-shopping">{t('checkout.continueShopping')}</Link>
            <Link to="/" className="btn-secondary py-3">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  // === Crypto Proof Submitted ===
  if (proofSubmitted && cryptoOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="glass-card p-12 text-center max-w-md" data-testid="crypto-proof-submitted">
          <div className="w-20 h-20 rounded-full bg-[#E8720C]/20 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-[#E8720C]" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Payment Pending Verification</h1>
          <p className="text-white/60 mb-2">Order: <span className="font-mono text-white">{cryptoOrder.order_id}</span></p>
          <p className="text-white/60 mb-8">Your proof has been submitted. Our team will verify your payment shortly.</p>
          <div className="flex flex-col gap-4">
            <Link to="/catalog" className="btn-primary py-3">Continue Browsing</Link>
          </div>
        </div>
      </div>
    );
  }

  // === Crypto Order Created ===
  if (cryptoOrder) {
    return (
      <div className="min-h-screen pt-24">
        <div className="container-custom py-12">
          <div className="max-w-2xl mx-auto">
            <button onClick={() => setCryptoOrder(null)} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to checkout
            </button>
            <div className="glass-card p-8" data-testid="crypto-payment-view">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-[#E8720C]/20 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-8 h-8 text-[#E8720C]" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Send {cryptoOrder.crypto_method.toUpperCase()} Payment</h2>
                <p className="text-white/50 text-sm">Order #{cryptoOrder.order_id}</p>
              </div>
              <div className="glass-card p-6 mb-6 text-center">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Amount to Send</p>
                <p className="text-4xl font-bold gold-text" data-testid="crypto-amount">${cryptoOrder.amount}</p>
                <p className="text-sm text-white/40 mt-1">in {cryptoOrder.crypto_method.toUpperCase()}</p>
              </div>
              <div className="glass-card p-6 mb-6">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-3">
                  {cryptoOrder.crypto_method.toUpperCase()} Wallet Address
                  {cryptoOrder.wallet_network && <span className="ml-2 text-[#E8720C]">({cryptoOrder.wallet_network})</span>}
                </p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-white/5 rounded-lg p-4 text-sm font-mono break-all" data-testid="crypto-wallet-address">
                    {cryptoOrder.wallet_address}
                  </code>
                  <button onClick={() => copyAddress(cryptoOrder.wallet_address)} className="p-3 rounded-xl bg-white/10 hover:bg-[#E8720C]/20 transition-colors flex-shrink-0" data-testid="copy-wallet-btn">
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                {copied && <p className="text-xs text-[#E8720C] mt-2">Copied to clipboard!</p>}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-white/60 mb-2">Transaction ID (TXID) or Proof</label>
                <input type="text" value={proofValue} onChange={(e) => setProofValue(e.target.value)}
                  placeholder="Paste your transaction ID here..."
                  className="w-full input-glass" data-testid="crypto-proof-input" />
              </div>
              {error && <div className="glass-card p-4 mb-6 border-red-500/20 bg-red-500/10"><p className="text-red-400 text-sm">{error}</p></div>}
              <button onClick={handleSubmitProof} disabled={loading || !proofValue.trim()}
                className="w-full btn-gold py-4 text-lg flex items-center justify-center gap-3" data-testid="submit-proof-btn">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : <><Upload className="w-5 h-5" /> Submit Payment Proof</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === Main Checkout View ===
  return (
    <div className="min-h-screen pt-24">
      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">
            <ShoppingBag className="inline w-8 h-8 mr-3" />
            {t('checkout.title')}
          </h1>

          {error && (
            <div className="glass-card p-6 mb-6 border-red-500/20 bg-red-500/10" data-testid="checkout-error">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-400" />
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          )}

          {itemDetails ? (
            <div className="glass-card p-8">
              {/* Order Summary */}
              <div className="flex gap-6 mb-8 pb-8 border-b border-white/10">
                {itemDetails.image && (
                  <img src={itemDetails.image} alt={itemDetails.name} className="w-24 h-24 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-2" data-testid="checkout-item-name">{itemDetails.name}</h2>
                  {!itemDetails.isCart && itemDetails.description && <p className="text-white/50 text-sm mb-2">{itemDetails.description}</p>}
                  {itemDetails.licenseLabel && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#E8720C]/10 text-[#E8720C]">
                      {licenseType === 'exclusive' ? <Crown className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                      {itemDetails.licenseLabel}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold gold-text" data-testid="checkout-item-price">
                    ${itemDetails.price % 1 === 0 ? itemDetails.price.toFixed(0) : itemDetails.price.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Cart Items List */}
              {cartItems.length > 0 && (
                <div className="mb-8 pb-8 border-b border-white/10">
                  <h3 className="text-sm font-medium text-white/50 mb-3">Tracks en tu carrito</h3>
                  <div className="space-y-2">
                    {cartItems.map((item, i) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg" data-testid={`cart-item-${item.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-white/20 w-5">{i + 1}</span>
                          <div>
                            <p className="text-sm font-medium text-white">{item.name}</p>
                            <p className="text-[10px] text-white/30">{item.genre}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {item.discount_pct ? (
                            <><span className="text-white/30 line-through text-xs mr-1">${item.original_price}</span><span className="text-[#E8720C] font-bold">${item.price}</span></>
                          ) : (
                            <span className="text-[#E8720C] font-bold">${item.price}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Info */}
              {user && (
                <div className="mb-8 pb-8 border-b border-white/10">
                  <h3 className="text-sm font-medium text-white/50 mb-3">{t('checkout.purchasingAs')}</h3>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-white/60 text-sm">{user.email}</p>
                </div>
              )}

              {/* Payment Method Selector */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-white/50 mb-3">{t('checkout.paymentMethod')}</h3>
                {isSubscriptionFlow ? (
                  <div className="glass-card p-4 flex items-center gap-3 ring-2 ring-[#0070BA] border-[#0070BA]/30" data-testid="payment-method-paypal-subscription">
                    <CreditCard className="w-5 h-5 text-[#0070BA]" />
                    <div className="text-left">
                      <p className="font-medium text-sm">PayPal · Suscripción recurrente</p>
                      <p className="text-xs text-white/40">Renovación automática mensual · Cancela cuando quieras</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`glass-card p-4 flex items-center gap-3 transition-all ${paymentMethod === 'paypal' ? 'ring-2 ring-[#0070BA] border-[#0070BA]/30' : ''}`}
                      data-testid="payment-method-paypal"
                    >
                      <CreditCard className={`w-5 h-5 ${paymentMethod === 'paypal' ? 'text-[#0070BA]' : 'text-white/50'}`} />
                      <div className="text-left">
                        <p className="font-medium text-sm">PayPal</p>
                        <p className="text-xs text-white/40">PayPal, Card, Debit</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setPaymentMethod('crypto')}
                      className={`glass-card p-4 flex items-center gap-3 transition-all ${paymentMethod === 'crypto' ? 'ring-2 ring-[#E8720C] border-[#E8720C]/30' : ''}`}
                      data-testid="payment-method-crypto"
                    >
                      <Wallet className={`w-5 h-5 ${paymentMethod === 'crypto' ? 'text-[#E8720C]' : 'text-white/50'}`} />
                      <div className="text-left">
                        <p className="font-medium text-sm">Crypto</p>
                        <p className="text-xs text-white/40">USDT / BTC</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Crypto Method Selector */}
              {!isSubscriptionFlow && paymentMethod === 'crypto' && (
                <div className="mb-8 pb-8 border-b border-white/10">
                  <h3 className="text-sm font-medium text-white/50 mb-3">Select Cryptocurrency</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setCryptoMethod('usdt')}
                      className={`glass-card p-4 text-center transition-all ${cryptoMethod === 'usdt' ? 'ring-2 ring-[#E8720C] border-[#E8720C]/30' : ''}`}
                      data-testid="crypto-select-usdt">
                      <p className="font-bold text-lg">USDT</p>
                      <p className="text-xs text-white/40">Tether (TRC20)</p>
                    </button>
                    <button onClick={() => setCryptoMethod('btc')}
                      className={`glass-card p-4 text-center transition-all ${cryptoMethod === 'btc' ? 'ring-2 ring-[#E8720C] border-[#E8720C]/30' : ''}`}
                      data-testid="crypto-select-btc">
                      <p className="font-bold text-lg">BTC</p>
                      <p className="text-xs text-white/40">Bitcoin</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Order Total */}
              <div className="flex justify-between items-center mb-8 pt-4 border-t border-white/10">
                <span className="text-lg font-medium">{t('checkout.total')}</span>
                <span className="text-3xl font-bold gold-text" data-testid="checkout-total">
                  ${itemDetails.price % 1 === 0 ? itemDetails.price.toFixed(0) : itemDetails.price.toFixed(2)}
                </span>
              </div>

              {/* PayPal Checkout */}
              {isSubscriptionFlow ? (
                paypalClientId ? (
                  <div data-testid="paypal-subscription-buttons-container">
                    <PayPalScriptProvider options={{
                      "client-id": paypalClientId,
                      vault: true,
                      intent: "subscription",
                      "enable-funding": "card",
                      "disable-funding": "credit,paylater",
                      currency: "USD",
                      components: "buttons",
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe", height: 48 }}
                        createSubscription={async () => {
                          try {
                            const r = await axios.post(`${API_URL}/api/paypal-subscriptions/create-membership`,
                              { tier_id: subscriptionTierId, origin_url: window.location.origin },
                              { withCredentials: true });
                            return r.data.subscription_id;
                          } catch (err) {
                            setError(err.response?.data?.detail || 'No se pudo iniciar la suscripción');
                            throw err;
                          }
                        }}
                        onApprove={(data) => {
                          window.location.href = `/paypal-subscription-success?subscription_id=${encodeURIComponent(data.subscriptionID)}&tier=${encodeURIComponent(subscriptionTierId)}`;
                        }}
                        onError={() => {
                          setError('Error al procesar la suscripción. Inténtalo de nuevo.');
                        }}
                        onCancel={() => {
                          setError('Suscripción cancelada. Puedes intentarlo cuando quieras.');
                        }}
                      />
                    </PayPalScriptProvider>
                    <p className="text-center text-[11px] text-white/40 mt-3" data-testid="paypal-card-hint">
                      ¿No tienes PayPal? Usa el botón <strong className="text-white/70">Tarjeta de débito o crédito</strong> para pagar con tu tarjeta directamente — sin crear cuenta.
                    </p>
                  </div>
                ) : (
                  <div className="glass-card p-6 text-center border-yellow-500/20 bg-yellow-500/5">
                    <p className="text-yellow-400 text-sm">PayPal aún no está configurado. Contacta soporte.</p>
                  </div>
                )
              ) : paymentMethod === 'paypal' && paypalClientId ? (
                <div data-testid="paypal-buttons-container">
                  <PayPalScriptProvider options={{
                    "client-id": paypalClientId,
                    currency: "USD",
                    intent: "capture",
                    "enable-funding": "card",
                    "disable-funding": "credit,paylater",
                    components: "buttons",
                  }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 50 }}
                      createOrder={async () => {
                        try {
                          const payload = itemType === 'cart'
                            ? { item_type: 'cart', cart_ids: cartIds.split(','), license_type: licenseType, origin_url: window.location.origin }
                            : { item_type: itemType, item_id: itemId, license_type: licenseType, origin_url: window.location.origin };
                          const res = await axios.post(`${API_URL}/api/checkout/create-session`, payload, { withCredentials: true });
                          return res.data.order_id;
                        } catch (err) {
                          setError(err.response?.data?.detail || 'Failed to create order');
                          throw err;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          await axios.post(`${API_URL}/api/checkout/capture/${data.orderID}`, {}, { withCredentials: true });
                          setPaymentSuccess(true);
                        } catch (err) {
                          setError('Payment capture failed. Please contact support.');
                        }
                      }}
                      onError={(err) => {
                        setError('PayPal checkout error. Please try again.');
                      }}
                      onCancel={() => {
                        setError('Payment was cancelled. You can try again.');
                      }}
                    />
                  </PayPalScriptProvider>
                  <p className="text-center text-[11px] text-white/40 mt-3" data-testid="paypal-card-hint-onetime">
                    ¿No tienes PayPal? Usa el botón <strong className="text-white/70">Tarjeta de débito o crédito</strong> para pagar directamente con tu tarjeta.
                  </p>
                </div>
              ) : paymentMethod === 'paypal' && !paypalClientId ? (
                <div className="glass-card p-6 text-center border-yellow-500/20 bg-yellow-500/5">
                  <p className="text-yellow-400 text-sm">PayPal is not configured yet. Please contact support or use crypto payment.</p>
                </div>
              ) : (
                <button onClick={handleCryptoOrder} disabled={loading || !wallets[cryptoMethod]}
                  className="w-full btn-gold py-4 text-lg flex items-center justify-center gap-3" data-testid="checkout-crypto-btn">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating order...</> : <><Wallet className="w-5 h-5" /> Pay with {cryptoMethod.toUpperCase()}</>}
                </button>
              )}

              <p className="text-center text-xs text-white/40 mt-4">{t('checkout.securePayment')}</p>
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              {loading ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : <p className="text-white/50">Loading checkout details...</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CheckoutCancelPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="glass-card p-12 text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-2xl font-bold mb-4">{t('checkout.cancelled')}</h1>
        <p className="text-white/60 mb-8">{t('checkout.cancelledMessage')}</p>
        <div className="flex flex-col gap-4">
          <Link to="/catalog" className="btn-primary py-3" data-testid="cancel-continue-shopping">{t('checkout.continueShopping')}</Link>
          <Link to="/" className="btn-secondary py-3" data-testid="cancel-back-home">{t('checkout.backToHome')}</Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
